import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { createBooking } from '../services/bookingService'
import type { Room } from '../services/roomService'

interface LocationState {
  room: Room
  bookingDetails?: {
    checkIn: string
    checkOut: string
    adults: number
    children: number
  }
}

const BookingPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const { room, bookingDetails: initialBookingDetails } = location.state as LocationState

  const [bookingDetails, setBookingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: initialBookingDetails?.checkIn || '',
    checkOut: initialBookingDetails?.checkOut || '',
    adults: initialBookingDetails?.adults || 1,
    children: initialBookingDetails?.children || 0,
    specialRequests: '',
  })

  // Calculate number of nights
  const calculateNights = () => {
    if (!bookingDetails.checkIn || !bookingDetails.checkOut) return 0
    const checkIn = new Date(bookingDetails.checkIn)
    const checkOut = new Date(bookingDetails.checkOut)
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
  }

  // Calculate total price
  const calculateTotalPrice = () => {
    if (!room) return 0
    const nights = calculateNights()
    const pricePerNight = room.discountedPrice || room.basePrice
    return pricePerNight * nights
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setBookingDetails(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!room) return

    // Validate form
    if (!bookingDetails.name || !bookingDetails.email || !bookingDetails.phone) {
      setError('Please fill in all required fields')
      return
    }

    // Navigate to payment page with booking details
    navigate('/payment', { 
      state: { 
        room,
        bookingDetails
      }
    })
  }

  const nights = calculateNights()
  const totalPrice = calculateTotalPrice()

  if (!room) {
    navigate('/rooms')
    return null
  }

  return (
    <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Complete Your Booking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please fill in your details to confirm your reservation
          </Typography>
        </Box>

        {success ? (
          <Alert 
            severity="success"
            sx={{ 
              mb: 2,
              alignItems: 'center',
              '& .MuiAlert-message': {
                flex: 1,
              },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Booking Confirmed!
            </Typography>
            <Typography variant="body2">
              Your booking has been successfully confirmed. You will receive a confirmation email shortly.
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={4}>
            {/* Booking Form */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <form onSubmit={handleSubmit}>
                  {/* Guest Information */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <PersonIcon color="primary" />
                    Guest Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12}>
                      <TextField
                        name="name"
                        label="Full Name"
                        value={bookingDetails.name}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="email"
                        label="Email"
                        type="email"
                        value={bookingDetails.email}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                        InputProps={{
                          startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="phone"
                        label="Phone"
                        value={bookingDetails.phone}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                        InputProps={{
                          startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Stay Details */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CalendarIcon color="primary" />
                    Stay Details
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="checkIn"
                        label="Check-in Date"
                        type="date"
                        value={bookingDetails.checkIn}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="checkOut"
                        label="Check-out Date"
                        type="date"
                        value={bookingDetails.checkOut}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="adults"
                        label="Adults"
                        type="number"
                        value={bookingDetails.adults}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        size="small"
                        inputProps={{ min: 1, max: room.capacity.adults }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="children"
                        label="Children"
                        type="number"
                        value={bookingDetails.children}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: room.capacity.children }}
                      />
                    </Grid>
                  </Grid>

                  {/* Special Requests */}
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <InfoIcon color="primary" />
                    Additional Information
                  </Typography>
                  <TextField
                    name="specialRequests"
                    label="Special Requests"
                    value={bookingDetails.specialRequests}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    placeholder="Any special requests or requirements?"
                  />

                  {error && (
                    <Alert 
                      severity="error" 
                      sx={{ mt: 2 }}
                    >
                      {error}
                    </Alert>
                  )}
                </form>
              </Paper>
            </Grid>

            {/* Booking Summary */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Booking Summary
                </Typography>
                
                {/* Room Details */}
                <Box sx={{ mb: 3 }}>
                  <img 
                    src={room.images[0]} 
                    alt={room.name}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '16px',
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {room.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {room.type}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Stay Details */}
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Check-in
                    </Typography>
                    <Typography variant="body1">
                      {bookingDetails.checkIn ? new Date(bookingDetails.checkIn).toLocaleDateString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Check-out
                    </Typography>
                    <Typography variant="body1">
                      {bookingDetails.checkOut ? new Date(bookingDetails.checkOut).toLocaleDateString() : '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1">
                      {nights} {nights === 1 ? 'night' : 'nights'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Guests
                    </Typography>
                    <Typography variant="body1">
                      {bookingDetails.adults} Adults, {bookingDetails.children} Children
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Price Breakdown */}
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Rate per night
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1">
                        {convertPrice(room.discountedPrice || room.basePrice)}
                      </Typography>
                      {room.discountedPrice && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            textDecoration: 'line-through',
                            color: 'text.secondary',
                            ml: 1
                          }}
                        >
                          {convertPrice(room.basePrice)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Number of nights
                    </Typography>
                    <Typography variant="body1">
                      {nights}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Total Amount
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {convertPrice(totalPrice)}
                    </Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      mt: 2,
                      p: 1.5,
                      bgcolor: 'info.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'info.100'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      color="info.main"
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <InfoIcon fontSize="small" />
                      GST will be applicable on the total amount
                    </Typography>
                  </Box>

                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      mt: 3,
                      py: 1.5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Continue to Payment'
                    )}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  )
}

export default BookingPage 